import {
	createFileRoute,
	Link,
	useNavigate,
	useSearch,
} from "@tanstack/react-router";
import {
	ArrowLeft,
	Loader2,
	MessageCircle,
	RefreshCw,
	XCircle,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { BrandButton } from "@/components/brand/brand-button";
import { BrandCard } from "@/components/brand/brand-card";
import { PublicHeader } from "@/components/public-layout";
import { useSession } from "@/hooks/auth-hooks";
import { parseInternalRedirect } from "@/lib/internal-redirect";
import type { BillingPeriod, SubscriptionTier } from "@/lib/plans";
import { normalizePublicPlan } from "@/lib/plans";
import { noIndexMeta } from "@/lib/seo";
import {
	SubscriptionCheckoutError,
	startSubscriptionCheckout,
} from "@/lib/subscription-checkout";

type PaidSubscriptionTier = Exclude<SubscriptionTier, "free">;

function normalizeCheckoutPlan(plan: unknown): PaidSubscriptionTier {
	const normalized = normalizePublicPlan(
		typeof plan === "string" ? plan : null,
	);
	return normalized === "max" ? "max" : "pro";
}

function redirectToLogin() {
	const redirect = `${window.location.pathname}${window.location.search}`;
	const params = new URLSearchParams({ redirect });
	window.location.assign(`/login?${params.toString()}`);
}

function buildCheckoutReturnUrl(
	url: string,
	redirect: string | null | undefined,
) {
	if (!redirect) return url;
	const nextUrl = new URL(url);
	nextUrl.searchParams.set("redirect", redirect);
	return nextUrl.toString();
}

function getErrorCopy(reasonKey: string) {
	const validReasons = ["cancelled", "failed", "expired", "unknown"];
	const key = validReasons.includes(reasonKey) ? reasonKey : "unknown";
	switch (key) {
		case "cancelled":
			return {
				title: "Checkout cancelled",
				description: "Your upgrade was cancelled before payment completed.",
			};
		case "failed":
			return {
				title: "Upgrade failed",
				description: "We could not complete your upgrade. Please try again.",
			};
		case "expired":
			return {
				title: "Checkout expired",
				description: "Your checkout session expired before it was completed.",
			};
		default:
			return {
				title: "Something went wrong",
				description:
					"We could not complete your upgrade. Please try again or contact support.",
			};
	}
}

export const Route = createFileRoute("/upgrade-error")({
	validateSearch: (search: Record<string, unknown>) => ({
		reason: (search.reason as string) || "unknown",
		plan: normalizeCheckoutPlan(search.plan),
		billingPeriod: search.billingPeriod === "yearly" ? "yearly" : "monthly",
		redirect: parseInternalRedirect(search.redirect),
	}),
	component: UpgradeErrorPage,
	head: () => ({
		meta: [{ title: "AgentReacher | Upgrade issue" }, ...noIndexMeta()],
	}),
});

function UpgradeErrorPage() {
	const navigate = useNavigate();
	const { reason, plan, billingPeriod, redirect } = useSearch({
		from: "/upgrade-error",
	});
	const { session, user, isPending } = useSession();
	const [isStartingCheckout, setIsStartingCheckout] = useState(false);
	const [checkoutError, setCheckoutError] = useState<string | null>(null);

	const resetCheckoutState = useCallback(() => {
		setIsStartingCheckout(false);
	}, []);

	useEffect(() => {
		const resetIfPageIsActive = () => {
			if (document.visibilityState === "visible") resetCheckoutState();
		};

		window.addEventListener("pageshow", resetCheckoutState);
		window.addEventListener("focus", resetCheckoutState);
		document.addEventListener("visibilitychange", resetIfPageIsActive);

		return () => {
			window.removeEventListener("pageshow", resetCheckoutState);
			window.removeEventListener("focus", resetCheckoutState);
			document.removeEventListener("visibilitychange", resetIfPageIsActive);
		};
	}, [resetCheckoutState]);

	const handleTryAgain = async () => {
		if (isPending || isStartingCheckout) return;
		if (!session || !user) {
			redirectToLogin();
			return;
		}

		setIsStartingCheckout(true);
		setCheckoutError(null);
		try {
			const checkoutUrl = await startSubscriptionCheckout({
				plan,
				billingPeriod: billingPeriod as BillingPeriod,
				successUrl: buildCheckoutReturnUrl(
					`${window.location.origin}/upgrade-success?plan=${encodeURIComponent(plan)}`,
					redirect,
				),
				cancelUrl: buildCheckoutReturnUrl(
					`${window.location.origin}/upgrade-error?reason=cancelled&plan=${encodeURIComponent(plan)}&billingPeriod=${encodeURIComponent(billingPeriod)}`,
					redirect,
				),
			});
			window.location.assign(checkoutUrl);
			window.setTimeout(resetCheckoutState, 1500);
		} catch (error) {
			console.error("[UpgradeError] Retry failed:", error);
			if (error instanceof SubscriptionCheckoutError && error.status === 401) {
				redirectToLogin();
				return;
			}

			setCheckoutError(
				error instanceof SubscriptionCheckoutError
					? error.message
					: "Failed to start checkout. Please try again.",
			);
			resetCheckoutState();
		}
	};

	const error = getErrorCopy(reason);

	return (
		<div className="min-h-svh bg-background">
			<PublicHeader />

			<main className="max-w-lg mx-auto px-4 py-16">
				<BrandCard variant="elevated" padding="lg" className="text-center">
					<div className="mx-auto mb-6 flex size-20 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
						<XCircle className="size-10 text-red-500" />
					</div>

					<h1 className="mb-2 text-2xl font-semibold text-foreground">
						{error.title}
					</h1>

					<p className="text-muted-foreground mb-8">{error.description}</p>

					<div className="space-y-3">
						{checkoutError ? (
							<p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900/40 dark:bg-red-950/30 dark:text-red-300">
								{checkoutError}
							</p>
						) : null}

						<BrandButton
							type="button"
							className="w-full"
							onClick={handleTryAgain}
							disabled={isPending || isStartingCheckout}
						>
							{isStartingCheckout ? (
								<Loader2 className="mr-2 size-4 animate-spin" />
							) : (
								<RefreshCw className="mr-2 size-4" />
							)}
							Try again
						</BrandButton>

						<BrandButton variant="secondary" className="w-full" asChild>
							<Link to="/dashboard/support">
								<MessageCircle className="mr-2 size-4" />
								Contact support
							</Link>
						</BrandButton>

						<button
							type="button"
							onClick={() => navigate({ to: "/dashboard" })}
							className="w-full text-sm text-muted-foreground hover:text-foreground transition-colors flex items-center justify-center gap-2"
						>
							<ArrowLeft className="size-4" />
							Back to dashboard
						</button>
					</div>
				</BrandCard>
			</main>
		</div>
	);
}
